/**
 * Palm Oracle Landing Page
 * Entry point for AI Palm Reading feature
 * Phase 2 - Minimum Vertical Slice
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Hand, Sparkles, ArrowLeft, ArrowRight, History, Zap, Star, Lock } from 'lucide-react';
import { checkPalmAccess, getPalmConfig, listReadings, PACKAGE_OPTIONS } from '../services/palmApi';
import '../styles/palm.css';

const PalmLandingPage = ({ credits = 0, user }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language === 'en' ? 'en' : 'tr';
  
  // Admin bypass check - admins have unlimited access
  const hasUnlimitedAccess = user?.is_admin === true || user?.role === 'admin';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [access, setAccess] = useState(null);
  const [config, setConfig] = useState(null);
  const [recentReadings, setRecentReadings] = useState([]);
  
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load access, config, and recent readings in parallel
      const [accessData, configData, readingsData] = await Promise.all([
        checkPalmAccess(token),
        getPalmConfig(),
        listReadings(token, 3, 0).catch(() => ({ readings: [] }))
      ]);
      
      setAccess(accessData);
      setConfig(configData);
      setRecentReadings(readingsData.readings || []);
    } catch (err) {
      console.error('Failed to load palm data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle no access
  if (!loading && access && !access.has_access) {
    if (access.show_coming_soon) {
      return (
        <div className="palm-container" data-testid="palm-landing">
          <div className="palm-coming-soon">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="coming-soon-icon">✋</div>
              <h1>Palm Oracle</h1>
              <p className="coming-soon-text">
                {lang === 'tr' 
                  ? 'Yakında sizlerle! AI destekli el falı deneyimi için hazırlanıyoruz.'
                  : 'Coming soon! We\'re preparing an AI-powered palm reading experience for you.'
                }
              </p>
              <Link to="/" className="palm-btn palm-btn-secondary">
                <ArrowLeft size={18} />
                {lang === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
              </Link>
            </motion.div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="palm-container" data-testid="palm-landing">
        <div className="palm-locked">
          <Lock size={48} />
          <h1>{lang === 'tr' ? 'Premium Özellik' : 'Premium Feature'}</h1>
          <p>
            {lang === 'tr'
              ? 'Palm Oracle\'a erişmek için Premium üyelik gereklidir.'
              : 'Premium membership required to access Palm Oracle.'
            }
          </p>
          <Link to="/?upgrade=palm" className="palm-btn palm-btn-primary">
            {lang === 'tr' ? 'Premium\'a Geç' : 'Upgrade to Premium'}
          </Link>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="palm-container" data-testid="palm-landing">
        <div className="palm-loading">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <Hand size={48} />
          </motion.div>
          <p>{lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="palm-container" data-testid="palm-landing">
        <div className="palm-error">
          <h2>{lang === 'tr' ? 'Bir hata oluştu' : 'An error occurred'}</h2>
          <p>{error}</p>
          <button onClick={loadData} className="palm-btn palm-btn-primary">
            {lang === 'tr' ? 'Tekrar Dene' : 'Try Again'}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="palm-container" data-testid="palm-landing">
      {/* Back Navigation */}
      <div className="palm-nav">
        <Link to="/" className="palm-back-link" data-testid="palm-back-link">
          <ArrowLeft size={20} />
          <span>{lang === 'tr' ? 'Ana Sayfa' : 'Home'}</span>
        </Link>
        <div className="palm-credit-badge" data-testid="palm-credit-badge">
          <span className="credit-icon">💎</span>
          <span>{hasUnlimitedAccess ? '∞' : credits}</span>
        </div>
      </div>
      
      {/* Hero Section */}
      <motion.div 
        className="palm-hero"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="palm-hero-icon">
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ 
              duration: 3, 
              repeat: Infinity, 
              repeatType: "reverse" 
            }}
          >
            ✋
          </motion.div>
        </div>
        <h1 className="palm-hero-title">Palm Oracle</h1>
        <p className="palm-hero-subtitle">
          {lang === 'tr' 
            ? 'AI destekli el falı ile avuç içinizin sırlarını keşfedin'
            : 'Discover the secrets of your palm with AI-powered reading'
          }
        </p>
      </motion.div>
      
      {/* Start New Reading CTA */}
      <motion.div
        className="palm-cta-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <button
          onClick={() => navigate('/palm/new')}
          className="palm-btn palm-btn-primary palm-btn-large"
          data-testid="start-palm-reading"
        >
          <Sparkles size={24} />
          <span>{lang === 'tr' ? 'Yeni El Falı Başlat' : 'Start New Palm Reading'}</span>
          <ArrowRight size={24} />
        </button>
        <p className="palm-cta-note">
          {lang === 'tr' 
            ? `${config?.costs?.standard || 5} kredi ile başlayın`
            : `Starting from ${config?.costs?.standard || 5} credits`
          }
        </p>
      </motion.div>
      
      {/* Package Options Preview */}
      <motion.div
        className="palm-packages"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <h2 className="palm-section-title">
          {lang === 'tr' ? 'Okuma Seçenekleri' : 'Reading Options'}
        </h2>
        <div className="palm-package-grid">
          {PACKAGE_OPTIONS.map((pkg, index) => (
            <motion.div
              key={pkg.value}
              className={`palm-package-card ${pkg.premium ? 'premium' : ''}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
            >
              <div className="package-header">
                <h3>{lang === 'tr' ? pkg.labelTr : pkg.labelEn}</h3>
                <div className="package-cost">
                  <span>{config?.costs?.[pkg.value] || pkg.cost}</span>
                  <span className="cost-icon">💎</span>
                </div>
              </div>
              <p className="package-desc">
                {lang === 'tr' ? pkg.descTr : pkg.descEn}
              </p>
              {pkg.premium && (
                <div className="package-badge">
                  <Star size={14} />
                  <span>Premium</span>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
      
      {/* Features Section */}
      <motion.div
        className="palm-features"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <h2 className="palm-section-title">
          {lang === 'tr' ? 'Özellikler' : 'Features'}
        </h2>
        <div className="palm-feature-grid">
          <div className="palm-feature-card">
            <div className="feature-icon">🔍</div>
            <h3>{lang === 'tr' ? 'AI Görüntü Analizi' : 'AI Image Analysis'}</h3>
            <p>
              {lang === 'tr' 
                ? 'Gelişmiş yapay zeka ile avuç içi çizgilerinizi analiz eder'
                : 'Analyzes your palm lines with advanced artificial intelligence'
              }
            </p>
          </div>
          <div className="palm-feature-card">
            <div className="feature-icon">🧬</div>
            <h3>Palm DNA™</h3>
            <p>
              {lang === 'tr' 
                ? 'Benzersiz el DNA profilinizi oluşturur'
                : 'Creates your unique palm DNA profile'
              }
            </p>
          </div>
          <div className="palm-feature-card">
            <div className="feature-icon">💡</div>
            <h3>{lang === 'tr' ? 'Kişisel Yorumlar' : 'Personal Insights'}</h3>
            <p>
              {lang === 'tr' 
                ? 'Size özel derinlemesine yorumlar sunar'
                : 'Provides in-depth personalized interpretations'
              }
            </p>
          </div>
          <div className="palm-feature-card">
            <div className="feature-icon">🔮</div>
            <h3>Oracle Fusion</h3>
            <p>
              {lang === 'tr' 
                ? 'Tarot ve kahve falı ile birleştirilmiş analiz'
                : 'Combined analysis with tarot and coffee reading'
              }
            </p>
          </div>
        </div>
      </motion.div>
      
      {/* Recent Readings */}
      {recentReadings.length > 0 && (
        <motion.div
          className="palm-recent"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h2 className="palm-section-title">
            <History size={20} />
            {lang === 'tr' ? 'Son Okumalarınız' : 'Your Recent Readings'}
          </h2>
          <div className="palm-readings-list">
            {recentReadings.map((reading) => (
              <Link
                key={reading.id}
                to={`/palm/reading/${reading.id}`}
                className="palm-reading-item"
              >
                <div className="reading-icon">✋</div>
                <div className="reading-info">
                  <span className="reading-date">
                    {new Date(reading.created_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')}
                  </span>
                  <span className={`reading-status status-${reading.status}`}>
                    {reading.status === 'completed' 
                      ? (lang === 'tr' ? 'Tamamlandı' : 'Completed')
                      : reading.status
                    }
                  </span>
                </div>
                <ArrowRight size={18} />
              </Link>
            ))}
          </div>
        </motion.div>
      )}
      
      {/* How It Works */}
      <motion.div
        className="palm-how-it-works"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
      >
        <h2 className="palm-section-title">
          {lang === 'tr' ? 'Nasıl Çalışır?' : 'How It Works'}
        </h2>
        <div className="palm-steps">
          <div className="palm-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>{lang === 'tr' ? 'Fotoğraf Çekin' : 'Take a Photo'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'Avuç içinizin net bir fotoğrafını çekin'
                  : 'Take a clear photo of your palm'
                }
              </p>
            </div>
          </div>
          <div className="palm-step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>{lang === 'tr' ? 'AI Analizi' : 'AI Analysis'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'Yapay zeka çizgilerinizi analiz eder'
                  : 'AI analyzes your palm lines'
                }
              </p>
            </div>
          </div>
          <div className="palm-step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>{lang === 'tr' ? 'Sonuçları Görün' : 'View Results'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'Detaylı Palm DNA ve yorumlarınızı alın'
                  : 'Get your detailed Palm DNA and interpretations'
                }
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PalmLandingPage;
